import { useCallback, useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { HorizontalScrollMenu } from '@hivebrite/design-system/HorizontalScrollMenu';
import { Skeleton } from '@hivebrite/design-system/Skeleton';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type {
  FormattedMenuItem,
  Topic,
} from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/types';
import { formatMenuItems } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/utils/formatMenuItems';
import { useTopicMenu } from 'hb-react/shared/apps/Topics/hooks/useTopicMenu';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import useGroupNavigationTheme from 'hb-react/shared/hooks/theme/useGroupNavigationTheme';

const MenuScrollable = ({ topic }: { topic: Topic }) => {
  const { currentUrl } = useDefaultAppContexts();
  const { appPages, isLoading } = useTopicMenu({ topic });
  const { whitelabelURL, topicId } = useTopicContext();

  const getPageUrl = useCallback(
    (path) =>
      dynamicTopicResourcePath(path, { whitelabelURL, topicId }, (pathRoute) =>
        path === pathRoute ? '/' : '',
      ),
    [whitelabelURL, topicId],
  );

  const groupNavigationTheme = useGroupNavigationTheme();
  const menuStyle = {
    backgroundColor: groupNavigationTheme.backgroundColor,
  };

  const menuItems: Array<FormattedMenuItem> = useMemo(
    () =>
      formatMenuItems({
        items: appPages,
        getPageUrl,
        currentUrl: currentUrl.origin,
      }),
    [appPages, currentUrl.origin, getPageUrl],
  );

  if (isLoading) {
    return (
      <Box data-testid="skeleton-container" display="flex" py={4}>
        <Skeleton width={100} height={19} mr={2} />
        <Skeleton width={120} height={19} mr={2} />
        <Skeleton width={90} height={19} mr={2} />
        <Skeleton width={110} height={19} />
      </Box>
    );
  }

  return (
    <HorizontalScrollMenu
      ariaLabel={s__('TopicAccessibility|Menu of %{groupName}').i__({
        groupName: topic.name,
      })}
      menuItems={menuItems}
      menuStyle={menuStyle}
    />
  );
};

export default MenuScrollable;
